/**
 * Utility functions for transforming course form data into API payload format
 */

import { GROUP_TRAINING_TYPE } from 'constant/course';
import { MasterArea } from 'services/api';

export interface CourseFormData {
  [key: string]: any;
}

export interface CoursePayload {
  basicInformation: Record<string, any>;
  deliveryDetails: Record<string, any>;
  indicatorStandards: Record<string, any>;
  reviewApproval: {
    flagReviewSME?: boolean;
    smeInternal?: Array<{ binusianID: string }>;
    smeExternal?: Array<{ email: string }>;
    picApproval?: Array<{ binusianID: string; approvalLevel: number }>;
  };
  activities?: Array<{
    activityID: string;
    activityName: string;
    activityType: string;
  }>;
  creatorCode?: string;
  // New top-level tnaCourses (moved from basicInformation)
  tnaCourses?: string[];
}

/**
 * Transform course form data into API payload format
 * @param data - Form data from react-hook-form
 * @param payload - Initial payload object to be populated
 * @returns Transformed payload object
 */

const groupTrainingTypeOptions = GROUP_TRAINING_TYPE;

export const transformCoursePayload = (
  data: CourseFormData,
  payload: CoursePayload,
  masterAreaList?: any,
  courseCategoryList?: any
): CoursePayload => {
  const basicInformation = [
    'courseName',
    'creatorCode',
    'courseCode',
    'courseType',
    'courseCategory',
    'competencyCategory',
    'description',
    'modeOfDelivery',
    'startDateTime',
    'endDateTime',
    'waReminder',
    'targetAudience',
    'programCategory',
    'specificJobBand',
    'learningContent',
    'levelOfTraining',
    'learningFormat',
    'trainingType',
    'fieldOrganizer',
    'room',
    'trainingLocation',
    'weightPreTest',
    'weightPostTest',
    'weightALP',
    'weightAttendance',
    'tags',
    'targetBinusian',
    'jenisTraining',
    'businessUnits',
    'status',
    'bcldLevel',
    'bcldStreamOrFunda'
  ] as const;

  const deliveryDetails = [
    'deliveryType',
    'learningObjectives',
    'groupTrainingType',
    'learningHours',
    'grpTraining'
  ] as const;

  const indicatorStandards = [
    'limIndicator',
    'liaIndicator',
    'passingFormula',
    'c',
    'formulaID'
  ] as const;

  // Transform basic information fields
  basicInformation.forEach((key) => {
    if (key in data) {
      if (key === 'businessUnits') {
        const businessUnits = data[key as keyof typeof data];
        payload.basicInformation = {
          ...payload.basicInformation,
          [key]:
            businessUnits === 'All' ||
            (Array.isArray(businessUnits) &&
              businessUnits.length === 1 &&
              businessUnits[0] === 'All')
              ? []
              : businessUnits
        };
        return;
      }
      if (key === 'tags') {
        payload.basicInformation = {
          ...payload.basicInformation,
          [key]: data[key as keyof typeof data] ? data[key as keyof typeof data].split(',') : []
        };
      } else {
        payload.basicInformation = {
          ...payload.basicInformation,
          [key]: data[key as keyof typeof data]
        };
      }
    }
  });

  // Transform delivery details fields
  deliveryDetails.forEach((key) => {
    if (key in data) {
      if (key === 'learningHours') {
        payload.deliveryDetails = {
          ...payload.deliveryDetails,
          [key]: Number(data[key as keyof typeof data])
        };
      } else {
        payload.deliveryDetails = {
          ...payload.deliveryDetails,
          [key]: data[key as keyof typeof data]
        };
      }
    }
  });

  // Transform indicator standards fields
  indicatorStandards.forEach((key) => {
    if (key in data) {
      payload.indicatorStandards = {
        ...payload.indicatorStandards,
        [key]: data[key as keyof typeof data]
      };
    }
  });

  if (data?.c && data?.c.length > 0) {
    payload.indicatorStandards = {
      ...payload.indicatorStandards,
      c: data.c.join(',')
    };
  } else {
    payload.indicatorStandards = {
      ...payload.indicatorStandards,
      c: ''
    };
  }

  // Transform review approval fields
  if ('flagReviewSME' in data) {
    payload.reviewApproval.flagReviewSME = data.flagReviewSME;
  }

  if ('smeInternal' in data && Array.isArray(data.smeInternal)) {
    const temp: any = [];
    data.smeInternal.forEach((id: any) => temp.push({ binusianID: id.split('-')[0] }));
    payload.reviewApproval.smeInternal = temp;
  }

  if ('smeExternal' in data && Array.isArray(data.smeExternal)) {
    const temp: any = [];
    data.smeExternal.forEach((id: any) => {
      const external = id.split('-');
      const email = external[external.length - 1];
      const name = external[0];
      return temp.push({ email: email || '-', name: name || '-' });
    });
    payload.reviewApproval.smeExternal = temp;
  }

  // Transform PIC approval fields
  const picApproval: Array<{ binusianID: string; approvalLevel: number }> = [];
  if ('picApproval1' in data && data.picApproval1) {
    picApproval.push({
      binusianID: data.picApproval1.split('-')[0] as string,
      approvalLevel: 1
    });
  }
  if ('picApproval2' in data && data.picApproval2) {
    picApproval.push({
      binusianID: data.picApproval2.split('-')[0] as string,
      approvalLevel: 2
    });
  }
  payload.reviewApproval.picApproval = picApproval;

  // Transform activities fields
  if ('activities' in data && Array.isArray(data.activities)) {
    payload.activities = data.activities.map((activity: any) => ({
      activityID: activity.activityId,
      activityName: activity.activityName,
      activityType: activity?.activityType || 'default'
    }));
  }
  if (masterAreaList && Array.isArray(masterAreaList)) {
    const area = masterAreaList.find((area: MasterArea) => area.areaID === data.creatorCode);
    payload.creatorCode = area?.areaCode || data.creatorCode;
  } else {
    payload.creatorCode = data.creatorCode;
  }

  if (courseCategoryList && Array.isArray(courseCategoryList)) {
    const category = courseCategoryList.find(
      (category: { id: number }) => category.id === Number(data.courseCategory)
    );
    payload.basicInformation.courseCategory = category?.name || '';
    payload.basicInformation.idCourseCategory = Number(data.courseCategory);
  } else {
    payload.basicInformation.courseCategory = data.courseCategory;
    payload.basicInformation.idCourseCategory = Number(data.courseCategory);
  }
  payload.basicInformation.jenisTraining =
    data?.jenisTraining ||
    groupTrainingTypeOptions.find((item) => item.label === data.groupTrainingType)?.value ||
    groupTrainingTypeOptions[0].value;

  return payload;
};
